import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, CircularProgress } from "@mui/material";
import PropTypes from "prop-types";

import { tariff as tariffOptions } from "common/enums";
import SelectField from "Components/Common/SelectField";
import {
  SUPER_ADMIN,
  powerAdmin1Roles,
  powerAdmin2Roles,
  powerAdmin3Roles,
  powerAdmin4Roles,
  applicationTypes,
} from "common/constant";

import { usePutUpdateTariffMutation } from "apis/Tariff";

import EditButton from "./styles";

const allowedApplicationStatusToEdit = [
  "Submitted",
  "Reviewed",
  "Approved",
  "Processed",
];

const allowedApplicationTypeIdsToEdit = [
  applicationTypes["Permanent Connection"],
  applicationTypes["Temporary Connection"],
  applicationTypes["Name Change/Transfer"],
  applicationTypes["Demand Reduction"],
  applicationTypes["Demand Extension"],
  applicationTypes["Premise Amalgamation"],
  applicationTypes["Premise Removal"],
  applicationTypes["Shifting of Service"],
  applicationTypes["Change of Category"],
  applicationTypes["Net Metering"],
  applicationTypes["Backup Power"],
  applicationTypes["Removal of Connection"],
];

const adminRoles = [
  { roles: powerAdmin1Roles, order: 4 },
  { roles: powerAdmin2Roles, order: 3 },
  { roles: powerAdmin3Roles, order: 2 },
  { roles: powerAdmin4Roles, order: 1 },
];

const getAdminApprovalOrder = (role) =>
  adminRoles.find(({ roles }) => roles.includes(role))?.order ?? 0;

const TariffField = ({
  applicationId,
  applicationTypeId,
  applicationStatusName = "",
  approvedBy,
  tariff: { name: tariffName = "", id: tariffValue = "" } = {},
  isGreenPower,
  isSolar,
}) => {
  const [isEditingTariff, setIsEditingTariff] = useState(false);
  const [tariff, setTariff] = useState(tariffValue);
  const { roles: loggedInAdminRole } = useSelector((state) => state.Login);

  const [changeTariff, { isLoading: isChangingTariff }] =
    usePutUpdateTariffMutation();

  const isTariffEditable = useMemo(
    () =>
      getAdminApprovalOrder(approvedBy) <
        getAdminApprovalOrder(loggedInAdminRole[0]) ||
      loggedInAdminRole.includes(SUPER_ADMIN),
    [approvedBy, loggedInAdminRole]
  );

  const updateTariff = async (query) => {
    try {
      await changeTariff(null, query);
      setIsEditingTariff(false);
    } catch (error) {}
  };

  const handleUpdateTariff = async () => {
    await updateTariff({
      applicationId,
      tariff,
    });
  };

  useEffect(() => {
    if (tariffValue && tariff !== tariffValue) {
      setTariff(tariffValue);
    }
  }, [tariffValue]);

  // If tariff name is not editable then return tariff name
  if (
    !allowedApplicationTypeIdsToEdit.includes(applicationTypeId) ||
    !allowedApplicationStatusToEdit.includes(applicationStatusName)
  ) {
    return (
      <>
        {tariffName ? (
          <>
            {tariffName} {isSolar && "(Net)"} {isGreenPower && "(Green Power)"}
          </>
        ) : (
          "-"
        )}
      </>
    );
  }

  // If tariff name is editable
  if (!isEditingTariff) {
    return tariffName ? (
      <>
        {tariffName ? (
          <>
            {tariffName} {isSolar && "(Net)"}
            {isGreenPower && "(Green Power)"}
          </>
        ) : (
          "-"
        )}
        {isTariffEditable && (
          <EditButton onClick={() => setIsEditingTariff(true)} />
        )}
      </>
    ) : (
      "-"
    );
  }

  // If tariff name is editable and edit button is clicked
  return (
    <div className="d-flex gap-2">
      <div className="flex-grow-1">
        <SelectField
          options={tariffOptions}
          value={tariffOptions.find(({ value }) => value === tariff) || ""}
          onChange={(_, { value }) => {
            setTariff(value);
          }}
          disabled={isChangingTariff}
        />
      </div>
      <Button
        variant="outlined"
        size="small"
        sx={{ height: "40px" }}
        disabled={isChangingTariff || tariff === tariffValue}
        onClick={handleUpdateTariff}
      >
        Save
        {isChangingTariff && <CircularProgress size={20} className="ms-2" />}
      </Button>
    </div>
  );
};

TariffField.propTypes = {
  applicationId: PropTypes.string,
  tariff: PropTypes.object,
  applicationTypeId: PropTypes.number,
  applicationStatusName: PropTypes.string,
  approvedBy: PropTypes.string,
  isGreenPower: PropTypes.bool,
  isSolar: PropTypes.bool,
};

export default TariffField;
